import { t } from "../../../../../utils/appString";
import type { TransactionDetails } from "../../../../../models/orders";

export const getTurnOver = (model: {
  orderPrice: number,
  totalQuantity: number,
  lotSize: number,
}): number => {
  const price = model.orderPrice ?? 0.0;
  const qty = model.totalQuantity * model.lotSize;
  return price * qty;
};

export function getBrokerage(model: TransactionDetails | null | undefined, turnOver: number, totalQuantity: number | undefined) {
  let res = 0.0;

  // Check if the model is valid
  if (!model || !turnOver || !totalQuantity) {
    console.error("Invalid inputs.");
    return res;
  }

  // Computation type is MUL (Multiply)
  if (model?.computation_type === "MUL") {
    res = (model.brokerage ? model.brokerage : 0.0) * (totalQuantity ? totalQuantity : 0);
  }
  // Computation type is PER (Percentage)
  else if (model?.computation_type === "PER" && model.maximum_brokerage !== -1) {
    // If brokerage percentage is applied
    const calculatedBrokerage = turnOver * (model.brokerage / 100);

    // Check if the calculated brokerage is less than the maximum brokerage
    if (calculatedBrokerage < model.maximum_brokerage) {
      res = calculatedBrokerage;
    } else {
      // Apply the maximum brokerage if calculated brokerage exceeds it
      res = model.maximum_brokerage; // This is the fix
    }
  } else {
    // If none of the conditions are met, apply percentage by default
    res = turnOver * (model.brokerage / 100);
  }

  // Return the final computed brokerage
  return res;
}

export function getExTrxCharges(
  model: TransactionDetails | null | undefined,
  turnOver: number
): number {
  if (!model) {
    return 0; // Return a default value instead of throwing an error
  }

  // Handle case where transaction_charges might be undefined
  const transactionCharges = model.transaction_charges || 0;

  // Calculate the result and ensure it's a number
  const res = turnOver * (transactionCharges / 100);

  // Return the result with proper precision to avoid floating-point issues
  return parseFloat(res.toFixed(4));
}

export function getSebiCharges(
  model: TransactionDetails | null | undefined,
  turnOver: number
): number {
  if (!model) {
    return 0; // Return a default value if model is null or undefined
  }

  // Handle case where sebiCharges might be undefined
  const sebiCharges = model.sebi_charges || 0;

  // Calculate the result and ensure it's a number
  const res = turnOver * (sebiCharges / 100);

  // Return the result with proper precision to avoid floating-point issues
  return parseFloat(res.toFixed(4));
}

export function getBrokerageStt(
  model: TransactionDetails | null | undefined,
  turnOver: number
): number {
  if (!model) {
    return 0; // Return a default value if model is null or undefined
  }

  // Handle case where stt might be undefined
  const stt = model.stt || 0;

  // Calculate the result and ensure it's a number
  const res = turnOver * (stt / 100);

  // Return the result with proper precision to avoid floating-point issues
  return parseFloat(res.toFixed(4));
}

//  SEEEE
export function getBrokerageGst(
  model: TransactionDetails | null | undefined,
  brokerage: number,
  exTrxCharge: number,
  sebiCharges: number
): number {
  if (!model || model.gst === undefined) {
    return 0; // Return default 0 if model or gst is missing
  }

  const gst = model.gst || 0;
  const res = (gst / 100) * (brokerage + exTrxCharge + sebiCharges);
  return parseFloat(res.toFixed(4)); // Ensure precision with 4 decimal places
}

export function getDpCharges(
  model: TransactionDetails | null | undefined
): number {
  if (!model || model.dp_charges === undefined) {
    return 0; // Return 0 if model or dpCharges is missing
  }

  return model.dp_charges; // Return dpCharges directly
}

export function getClearingCharges(
  model: TransactionDetails | null | undefined
): number {
  if (!model || model.clearing_charges === undefined) {
    return 0; // Return 0 if model or clearingCharges is missing
  }

  return model.clearing_charges; // Return clearingCharges directly
}

export function getStampDuty(
  model: TransactionDetails | null | undefined,
  turnOver: number
): number {
  if (!model || model.stamp_duty === undefined) {
    return 0; // Return 0 if model or stampDuty is missing
  }

  const stampDuty = model.stamp_duty || 0;
  const res = (stampDuty / 100) * turnOver;
  return parseFloat(res.toFixed(4)); // Ensure precision with 4 decimal places
}



/**
 * Calculate and accumulate all charges
 */
export function getCumulativeCharges(
  bModel: TransactionDetails,
  turnOver: number,
  totalQuantity: number | undefined
): Array<{ label: string; value: number }> {
  return [
    { label: t('charges.exchangeTurnover'), value: turnOver },
    { label: t('charges.brokerageBuy'), value: getBrokerage(bModel, turnOver, totalQuantity) },
    { label: t('charges.exchangeTransCharges'), value: getExTrxCharges(bModel, turnOver) },
    { label: t('charges.sebiCharges'), value: getSebiCharges(bModel, turnOver) },
    { label: t('charges.securitiesTransactionCharges'), value: getBrokerageStt(bModel, turnOver) },
    { label: t('charges.gstChargesText'), value: getBrokerageGst(bModel, getBrokerage(bModel, turnOver, totalQuantity), getExTrxCharges(bModel, turnOver), getSebiCharges(bModel, turnOver)) },
    { label: t('charges.dpCharges'), value: getDpCharges(bModel) },
    { label: t('charges.clearingCharges'), value: getClearingCharges(bModel) },
    { label: t('charges.stampDuty'), value: getStampDuty(bModel, turnOver) },
    {
      label: t('charges.totalTaxChargesOnBuy'),
      value: [
        getBrokerage(bModel, turnOver, totalQuantity),
        getExTrxCharges(bModel, turnOver),
        getBrokerageStt(bModel, turnOver),
        getSebiCharges(bModel, turnOver),
        getBrokerageGst(bModel, getBrokerage(bModel, turnOver, totalQuantity), getExTrxCharges(bModel, turnOver), getSebiCharges(bModel, turnOver)),
        getDpCharges(bModel),
        getClearingCharges(bModel),
        getStampDuty(bModel, turnOver),
      ].reduce((acc, charge) => acc + charge, 0),
    },
  ];
}